/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * BreadCrumbs Component
 * ------------
 * Dynamically generates breadcrumb navigation based on the current URL path.
 *
 * Key Features:
 * • Automatically splits the current route into breadcrumb segments
 * • Uses a readable label map for common routes (Dashboard, Applications, Users, etc.)
 * • Theme-aware styling for clickable breadcrumb links
 * • Hides internal route IDs (e.g., /dashboard/users/17/update → hides "17")
 * • Special handling for role update route:
 *      - Hides the numerical roleId segment
 *      - Shows “Update” instead of raw segment name
 * • Final breadcrumb segment is displayed as plain text (non-clickable)
 *
 * Purpose:
 * Provides clean, user-friendly breadcrumb navigation across the dashboard,
 * improving navigation clarity and user orientation within nested pages.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useQuery } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

function AppNameResolver({ id }) {
    const { data } = useQuery({
        queryKey: ['applicationDetails', id],
        queryFn: async () => {
            const response = await apiHelper.apiCall(`/arcee/application/details/${id}/`);
            if (!response.ok) throw new Error('App not found');
            return response.json();
        },
        staleTime: 600000, // 10 mins
    });
    return data?.name || id;
}

function GroupNameResolver({ groupId, appId }) {
    // There is no single-group GET endpoint, so we fetch the same group list the
    // page itself uses and pick our group out client-side. Sharing this
    // queryKey shape with any other list consumer lets react-query dedupe the
    // request within staleTime.
    const { data } = useQuery({
        queryKey: ['groupsList', appId],
        queryFn: async () => {
            const response = await apiHelper.apiCall(`/arcee/groups/?application_id=${appId}`);
            if (!response.ok) throw new Error('Groups not found');
            return response.json();
        },
        staleTime: 600000, // 10 mins
    });
    const groups = Array.isArray(data) ? data : (data?.results || []);
    const group = groups.find((g) => String(g.id) === String(groupId));
    return group?.name || groupId;
}

function TableNameResolver({ tableId, appId }) {
    const { data } = useQuery({
        queryKey: ['tableDetails', appId, tableId],
        queryFn: async () => {
            const response = await apiHelper.apiCall(
                `/arcee/application/table/details/`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        application_id: Number(appId),
                        table_id: Number(tableId),
                    }),
                }
            );
            if (!response.ok) throw new Error('Table not found');
            return response.json();
        },
        staleTime: 600000, // 10 mins
    });
    return data?.display_name || data?.name || tableId;
}

const routeMap = {
    dashboard: 'Dashboard',
    applications: 'Applications',
    settings: 'Settings',
    create: 'Create',
    marketplace: 'Marketplace',
    'my-packages': 'My Packages',
    users: 'Users',
    table_create: 'Create Table',
    roles: 'Roles',
    permissions: 'Permissions',
    profile: 'Profile',
    'notification-analytics': 'Notification Analytics',
    'notification-controls': 'Notification Controls',
    'group-types': 'Group Structure',
    'groups': 'Groups',
    'public-pages': 'Public Pages',
    'page-builder': 'Page Builder',
    edit: 'Edit',
    submissions: 'Submissions',
    'ownership-relations': 'Ownership Relations',
};

function BreadCrumbs() {
    const { theme } = useTheme();
    const location = useLocation();
    const paths = location.pathname.split('/').filter(Boolean);

    // No breadcrumbs on plain /dashboard
    if (paths.length === 1) return null;

    return (
        <nav aria-label="breadcrumb" className="mb-4">
            <ol className="breadcrumb">
                {paths.map((segment, idx) => {
                    const url = '/' + paths.slice(0, idx + 1).join('/');
                    const isLast = idx === paths.length - 1;

                    const parent = idx > 0 ? paths[idx - 1] : null;
                    const grandParent = idx > 1 ? paths[idx - 2] : null;

                    const isNumericId = /^\d+$/.test(segment);

                    // Hide numeric IDs after "users", "roles", "page-builder", or "forms".
                    // Hide "configure" and "forms" which are internal routing artifacts.
                    const shouldHideId =
                        (isNumericId && ['users', 'roles', 'page-builder', 'forms'].includes(parent)) ||
                        segment === 'configure' ||
                        segment === 'forms';

                    if (shouldHideId) return null;

                    // Resolve application numeric ID to app name
                    let label =
                        segment === 'update' && isLast
                            ? 'Update'
                            : routeMap[segment] || segment;

                    if (isNumericId && parent === 'applications') {
                        label = <AppNameResolver id={segment} />;
                    }

                    // Resolve table numeric ID under public-pages to table display name.
                    // Rendered as plain text (last visible crumb on the configure page).
                    const isPublicTableId = isNumericId && parent === 'public-pages';
                    if (isPublicTableId) {
                        const appId = idx >= 2 ? paths[idx - 2] : null;
                        return (
                            <li key={url} className="breadcrumb-item active" aria-current="page">
                                <TableNameResolver tableId={segment} appId={appId} />
                            </li>
                        );
                    }

                    // Resolve a group numeric ID (the segment right after "groups")
                    // to the group's name. Rendered as plain, non-clickable text —
                    // there is no standalone single-group route to link to, so a
                    // <Link> here would point at a dead route. This keeps the
                    // friendly name (unlike hiding the id) without the broken link.
                    const isGroupId = isNumericId && parent === 'groups';
                    if (isGroupId) {
                        const appId = idx >= 2 ? paths[idx - 2] : null;
                        return (
                            <li
                                key={url}
                                className={`breadcrumb-item${isLast ? ' active' : ''}`}
                                aria-current={isLast ? 'page' : undefined}
                            >
                                <GroupNameResolver groupId={segment} appId={appId} />
                            </li>
                        );
                    }

                    // Table name segment (directly inside an application, not a known route)
                    const isTableName =
                        grandParent === 'applications' &&
                        /^\d+$/.test(parent) &&
                        !routeMap[segment];

                    return (
                        <li
                            key={url}
                            className={`breadcrumb-item${isLast ? ' active' : ''}`}
                            aria-current={isLast ? 'page' : undefined}
                        >
                            {isLast || isTableName ? (
                                label
                            ) : (
                                <Link
                                    to={url}
                                    style={{
                                        color: theme.primary,
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                    }}
                                >
                                    {label}
                                </Link>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}

export default BreadCrumbs;